import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdHelp } from "react-icons/md";

const AcdTooltip = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const tooltipRef = useRef(null);

  const updatePosition = () => {
    if (buttonRef.current && tooltipRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      // Position above the button
      const top = buttonRect.top - tooltipRect.height - 10;
      let left = buttonRect.left + buttonRect.width / 2 - tooltipRect.width / 2;
      
      // Keep tooltip within viewport
      if (left < 10) left = 10;
      if (left + tooltipRect.width > viewportWidth - 10) {
        left = viewportWidth - tooltipRect.width - 10;
      }
      
      setPosition({ top, left });
    }
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isVisible]);

  if (!data?.acd_detail) {
    return null;
  }
  
  const {
    acd_code,
    acd_description,
    FMCSR_Category,
    FMCSR_Description,
    NDR_Category,
    NDR_Description,
    match_type,
    confidence,
    matched_code
  } = data.acd_detail;

  const matchTypeString = String(match_type || '').toLowerCase();
  const isSemanticMatch = matchTypeString === 'semantic';
  const confidencePercent = confidence ? 
    `${(Number(confidence) * 100).toFixed(0)}%` : 
    null;

  const tooltipContent = (
    <div 
      ref={tooltipRef}
      className="fixed z-[999999] bg-white shadow-lg rounded-lg p-3 w-80 border border-gray-200
                 transition-opacity duration-200
                 after:content-[''] after:absolute after:top-full after:left-1/2 
                 after:-translate-x-1/2 after:border-8 
                 after:border-t-white after:border-x-transparent after:border-b-transparent"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
      }}
    >
      <div className="text-xs">
        <div className="font-semibold border-b border-gray-100 pb-1 mb-2 flex justify-between">
          <span>ACD Code Details</span>
          <span className={`${isSemanticMatch ? "text-amber-600" : "text-green-600"} font-medium px-1.5 py-0.5 rounded-sm ${isSemanticMatch ? "bg-amber-50" : "bg-green-50"}`}>
            {isSemanticMatch ? "AI Match" : "Direct Match"}
          </span>
        </div>
        
        <div className={`text-[11px] p-2 mb-2 rounded ${isSemanticMatch ? "bg-amber-50" : "bg-green-50"}`}>
          {isSemanticMatch 
            ? `This violation code (${acd_code}) was semantically matched with ACD code ${matched_code || ''}${confidencePercent ? ` with ${confidencePercent} confidence` : ''}.`
            : "This violation has a direct match with an ACD code."}
        </div>
        
        <div className="space-y-2">
          <div className="flex">
            <span className="font-medium w-24 text-gray-600">Original Code:</span>
            <span className="text-gray-800">{acd_code}</span>
          </div>
          
          {isSemanticMatch && matched_code && (
            <div className="flex">
              <span className="font-medium w-24 text-gray-600">Matched Code:</span>
              <span className="text-amber-700 font-medium">{matched_code}</span>
            </div>
          )}
          
          <div className="flex flex-col gap-1">
            <span className="font-medium text-gray-600">Description:</span>
            <span className="text-gray-800 text-[11px]">{acd_description || `ACD code ${isSemanticMatch ? matched_code : acd_code}`}</span>
          </div>
          
          {FMCSR_Category && FMCSR_Category !== '--' && (
            <div className="flex flex-col gap-1 mt-1 pt-1 border-t border-gray-100">
              <span className="font-medium text-gray-600">FMCSR Category:</span>
              <span className="text-gray-800 text-[11px]">{FMCSR_Category}</span>
              {FMCSR_Description && FMCSR_Description !== '--' && (
                <span className="text-gray-600 text-[10px]">{FMCSR_Description}</span>
              )}
            </div>
          )}
          
          {NDR_Category && NDR_Category !== '--' && (
            <div className="flex flex-col gap-1 mt-1 pt-1 border-t border-gray-100">
              <span className="font-medium text-gray-600">NDR Category:</span>
              <span className="text-gray-800 text-[11px]">{NDR_Category}</span>
              {NDR_Description && NDR_Description !== '--' && (
                <span className="text-gray-600 text-[10px]">{NDR_Description}</span>
              )}
            </div>
          )}
          

        </div>
      </div>
    </div>
  );

  return (
    <>
      <button 
        ref={buttonRef}
        type="button"
        className={`focus:outline-none ${isSemanticMatch ? "text-amber-500" : "text-blue-500"} hover:opacity-80`}
        aria-label="ACD Code details"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        <MdHelp size={16} />
      </button>
      
      {isVisible && createPortal(tooltipContent, document.body)}
    </>
  );
};

export default AcdTooltip;